import {IObservable, has} from "mobx";
import {observer} from "mobx-react";
import React, {useContext, useState} from 'react';

import FontAwesome from "../../utilities/FontAwesome";
import useAsyncEffect from "../../../hooks/useAsyncEffect";
import {StoreContext} from "../../../stores/StoreLoader";
import {
    getDistrict,
    subOrganizationTypesByParent, subOrganizationTypesForStaffBlock,
} from "../../../utils/SchoolBlocksUtilities";
import {ConnectionFactory, StaffController} from "../../../backends/YiiRoutes";
import { undefOrNull } from "../../../utils/booleanUtils";
import { updateUserModel } from "./UpdateUserProfile";
import NotificationManager from "../../notifications/NotificationManager";

interface MinimalDepartment {
    id: string,
    title: string,
}

export const LocationsCard = observer((props: {
    blockObj: IPersonBlock,
}) => {
    const {i18nStore, organizationStore} = useContext(StoreContext);
    const [schoolsAndDistricts, setSchoolsAndDistricts] = useState<BaseOrganization[]>([]);
    const [departments, setDepartments] = useState<Array<MinimalDepartment | BaseOrganization>>([]);
    const [selectedDeptId, setSelectedDeptId] = useState<string>("");
    const [selectedOrgId, setSelectedOrgId] = useState<string>("");

    useAsyncEffect(async () => {
        const district = getDistrict(organizationStore.organization);

        const data: BaseOrganization[] = [];
        data.push(district);

        if (district.type === "district") {
            const result = await organizationStore.fetchClient("organizations", "organizations_list", {
                path__descendant_or_eq: district.path,
                type__in: ["school"],
                omit: ["url", "hostname"],
                deleted: false,
                ordering: "title",
                page_size: 200,
            });
            if (result) {
                // @ts-ignore
                data.push(...JSON.parse(result.data).data);
            }
        }

        setSchoolsAndDistricts(data);
    }, []);

    useAsyncEffect(async () => {
        if (selectedOrgId) {
            setDepartments([]);
            const selectedOrg = schoolsAndDistricts.find(s => s.id === selectedOrgId);
            const result = await organizationStore.fetchClient("organizations", "organizations_list", {
                path__descendant_or_eq: selectedOrg?.path,
                type__in: subOrganizationTypesForStaffBlock(selectedOrg?.type as OrganizationTypesWithGrids),
                omit: ["url", "hostname"],
                deleted: false,
                ordering: "title",
                page_size: 200,
            });
            const data: MinimalDepartment[] = [];
            if (selectedOrg?.type === "district") {
                data.push({
                    id: "null",
                    title: "Staff (staff listing)",
                })
            } else {
                data.push({
                    id: "1",
                    title: 'Teacher (staff listing)',
                }, {
                    id: "2",
                    title: 'Staff (staff listing)',
                });
            }
            // @ts-ignore
            data.push(...JSON.parse(result.data).data);
            setDepartments(data);
        }
    }, [selectedOrgId]);

    function createDepartment(department, organization) {
        let staffType = 2, department_id = department.id;

        // School organizations list the staff type as a department with ID 1 or 2, so we want to make sure that info
        // goes in the right place
        if (department_id === "1" || department_id === "2") {
            staffType = department_id;
            department_id = null;
        }

        ConnectionFactory(
            organizationStore.currentOrganization.id,
            StaffController.departments(i18nStore.currentOrgSupportedLocale, props.blockObj.blockModel.id, "create"),
            {
                method: 'POST',
                data: {
                    organization_id: organization.id,
                    staff_type: staffType,
                    department: department_id,
                },
            }
        ).done(function (result) {
            if (result.type === 'success') {
                props.blockObj.setAttributes({
                    blockModel: {
                        ...props.blockObj.blockModel,
                        staffMemberProfiles: [
                            ...props.blockObj.blockModel.staffMemberProfiles,
                            result.staffMemberProfile,
                        ],
                    }
                })
            }

            NotificationManager[result.type](result.text);
        });
    }

    function handleRemoveOrRestore(action, primary_key) {
        ConnectionFactory(
            organizationStore.currentOrganization.id,
            StaffController.departments(i18nStore.currentOrgSupportedLocale, props.blockObj.blockModel.id, action),
            {
                method: 'POST',
                data: {
                    primary_key,
                },
            },
        ).done(function (result) {
            const newProfiles: StaffMemberProfile[] = props.blockObj.blockModel.staffMemberProfiles.map(l => {
                if (l.primary_key === primary_key) {
                    return result.staffMemberProfile;
                }
                return l;
            });
            props.blockObj.setAttributes({
                blockModel: {
                    ...props.blockObj.blockModel,
                    staffMemberProfiles: newProfiles
                }
            })

            NotificationManager[result.type](result.text);
        });
    }

    if (!has(props.blockObj.blockModel, "staffMemberProfiles") || props.blockObj.blockModel.staffMemberProfiles.length === 0) {
        return <div/>;
    }

    return <>
        <hr style={{borderTop: "1px solid rgba(0, 0, 0, 0.3)"}}/>
        <div className="sb-settings-block-content-heading" style={{marginTop: "1.5em", marginBottom: "0.5em"}}>
            <h4>Card Locations</h4>
        </div>
        <div className="card-locations">
            <div className="table-responsive">
                <table className="locations table table-condensed table-striped">
                    <tbody>
                        {props.blockObj.blockModel.staffMemberProfiles
                        // auto-deleted profile cards should not be displayed.  All other cards should be displayed.
                        // This way, if a user deletes an auto-created card, it changes it to synced = false and
                        // still shows up in this list.  Restoring the card does not change the synced attribute.
                        .filter(staffProfile => {
                            return !undefOrNull(staffProfile.organization)
                                && (!staffProfile.deleted || (staffProfile.deleted === true && staffProfile.synced === false));
                        })
                        .map(staffProfile => {
                            let department = "";
                            if (!staffProfile.department_id) {
                                if (staffProfile.organization.type === "district" || staffProfile.type === 2) {
                                    department = "Staff (staff listing)";
                                } else if (staffProfile.type === 1) {
                                    department = "Teacher (staff listing)";
                                }
                            } else {
                                department = staffProfile.department.title;
                            }

                            return <tr className="active" key={staffProfile.primary_key}
                                       style={staffProfile.deleted === true ? {
                                           textDecoration: "line-through",
                                           color: "#888",
                                       } : {}}>
                                <td className={"notranslate"}>{staffProfile.organization.title}</td>
                                <td>
                                    {department}
                                </td>
                                <td>
                                    <button
                                        className="remove-department btn btn-default btn-xs"
                                        title={staffProfile.deleted ? "Restore" : "Delete"}
                                        onClick={() => handleRemoveOrRestore(staffProfile.deleted ? "restore" : "delete", staffProfile.primary_key)}
                                    >
                                        <span className="sr-only">{staffProfile.deleted ? "Restore" : "Delete"}</span>
                                        <FontAwesome prefix={'fas'}
                                                     name={staffProfile.deleted ? "fa-undo" : "fa-times-circle"}/>
                                    </button>
                                </td>
                            </tr>
                        })}
                    </tbody>
                </table>
            </div>
        </div>
        <div className="sb-teacher-locations row">
            <div className="col-md-4">
                <select
                    value={selectedOrgId}
                    onChange={e => setSelectedOrgId(e.target.value)}
                    className={'form-control sb-school-district'}
                    title={"School or District list"}
                >
                    <option key="header" value={""}>Select School or District</option>
                    {schoolsAndDistricts.map(org => {
                        return <option key={org.id} value={org.id}>{org.title}</option>
                    })}
                </select>
            </div>
            <div className="col-md-4">
                <select
                    value={selectedDeptId}
                    onChange={e => setSelectedDeptId(e.target.value)}
                    title={"Department list"}
                    className={'form-control sb-department'}
                    disabled={!selectedOrgId}
                >
                    <option key="header" value={""}>Select a Department/Group/Team</option>
                    {departments.map(dept => <option key={dept.id} value={dept.id}>{dept.title}</option>)}
                </select>
            </div>
            <div className="col-md-4">
                <button
                    disabled={!selectedDeptId}
                    className="btn btn-default sb-add-location"
                    style={{margin: "0"}}
                    onClick={() => {
                        const selectedOrg = schoolsAndDistricts.find(s => s.id === selectedOrgId);
                        const selectedDept = departments.find(d => d.id === selectedDeptId);
                        createDepartment(selectedDept, selectedOrg);
                    }}
                >Add New Location
                </button>
            </div>
        </div>
        <hr style={{borderTop: "1px solid rgba(0, 0, 0, 0.3)"}}/>
    </>
})
